import React from 'react'
import { graphql } from 'gatsby'

// Shared Components
import {
  HeaderBlock,
  OverlapBlock,
  CtaButton,
  SplitScreen,
  InfiniteCards,
  HerolessQuote,
} from 'shared-ui'

// Local Features
import Layout from '../features/Layout'
import Seo from '../features/Seo'

import TopG from '../assets/landing-graphic-top.svg'

import { Diversity, Adjustable, SkillDemand } from '../primitives/Icons'
import CardlessTrifold from '../features/CardlessTrifold'

// Image Data
export const query = graphql`
  query LandingPageQuery {
    landing: file(relativePath: { eq: "landingHero.png" }) {
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH)
      }
    }
    g1: file(relativePath: { eq: "landingG1.png" }) {
      childImageSharp {
        gatsbyImageData(width: 423, quality: 100, layout: CONSTRAINED)
      }
    }
    g2: file(relativePath: { eq: "landingG2.png" }) {
      childImageSharp {
        gatsbyImageData(width: 664, quality: 100, layout: CONSTRAINED)
      }
    }
    g3: file(relativePath: { eq: "landingG3.png" }) {
      childImageSharp {
        gatsbyImageData(width: 664, quality: 100, layout: CONSTRAINED)
      }
    }
    g4: file(relativePath: { eq: "landingG4.png" }) {
      childImageSharp {
        gatsbyImageData(width: 664, quality: 100, layout: CONSTRAINED)
      }
    }
    g5: file(relativePath: { eq: "landingG5.png" }) {
      childImageSharp {
        gatsbyImageData(width: 753, quality: 100, layout: CONSTRAINED)
      }
    }
    g6: file(relativePath: { eq: "landingG6.png" }) {
      childImageSharp {
        gatsbyImageData(width: 827, quality: 100, layout: CONSTRAINED)
      }
    }
    avatar: file(relativePath: { eq: "melissaProfile.png" }) {
      childImageSharp {
        gatsbyImageData(width: 81, quality: 100, layout: FIXED)
      }
    }
  }
`

const IndexPage = ({ data }) => {
  const homePageData = {
    hero: {
      heroText: 'Helping build a 21st century workforce',
      bgColor: '#f9faff',
      pad: '4rem 2rem',
      max: '550px',
      headMax: '465px',
      breakP: '1052px',
      innerMax: '1300px',
      slug: '/contact/',
      subText: (
        <p>
          We use graph theory and ML to curate technical and creative teams from
          the top 5% of the world’s best univerities to build and manage highly
          skilled workforces for leading companies.
        </p>
      ),
      graphic: data.landing.childImageSharp.gatsbyImageData,
      vectors: [
        {
          vector: <TopG />,
          styles: { top: '0', left: '0', position: 'absolute' },
        },
      ],
    },
    footer: {
      head: `We embed the world’s top minds with the latest skillsets onto your team`,
      buttons: <CtaButton slug={'/contact/'}>Build Your Dream Team</CtaButton>,
    },
    cardless: [
      {
        icon: <SkillDemand />,
        head: 'In Demand Skills',
        sub:
          'The pace of change is so fast, companies cannot grow all of their talent in-house. Our Fellows have the exact skills you need.',
      },
      {
        icon: <Adjustable />,
        head: 'Adjustable & Variable',
        maxW: '330px',
        sub:
          'Avoid fixed costs in a variable world for 30% less than staffing firms. And each year, spin us up or down as needed.',
      },
      {
        icon: <Diversity />,
        head: 'Diversity',
        sub:
          'The challenges of tomorrow will require diverse minds and backgrounds. Tap into our pool of diverse talent to solve them.',
      },
    ],
    infiniteCards1: [
      {
        id: 'a1',
        head: '04. Fully Managed and an Opportunity for Conversion',
        sub:
          'ProMazo Managers oversee the work of the Fellows on a day-to-day basis so you don’t have to. Upon completion, you have the option of converting your Fellows to full time if it makes sense for your organization.',
      },
      {
        id: 'b1',
        head: '05. Not Having a Birds-Eye View of Everything',
        sub:
          "See the status of everything all in one place. And don't worry. Even if you do forget, we'll give you a little nudge.",
      },
      {
        id: 'c1',
        head: '01. Company Identifies Their Needs',
        sub:
          'We work with your team to understand your needs, the culture of your team and develop a talent roadmap to ensure we are meeting your needs now and in the future.',
      },
      {
        id: 'd1',
        head: '02. Fellows Are Selected and Onboarded',
        sub:
          'ProMazo Chapter Leaders scour our database of thousands to find your perfect team. Fellows are selected based on technical competency and soft skills. We onboard and train them to ensure they can start adding value from Day 1.',
      },
      {
        id: 'e1',
        head: '03. Embedded Within Your Team',
        sub:
          'Your team of ProMazo Fellows are embedded directly into your team’s workflow. Through combining the latest academic insights in behavioral psychology with best business practices (Agile Development Methodology, Total Motivation Theory & Deep Work), we developed the ProMazo framework to deliver highly productive remote teams drawn from the best universities in the world.',
      },
    ],
  }

  return (
    <Layout pageData={homePageData}>
      <Seo title="Home" />

      <HeaderBlock
        headerText="We're going to let you in on a little secret"
        pad="8rem 0 12rem"
        mobilePad="4rem 1.8rem 3.6rem"
      />

      <OverlapBlock
        head="Universities are an untapped talent pool"
        sub="With their cutting edge skill sets, flexible work patterns and diversity, they fit every characteristic companies need in their modern workforce."
        buttons={<CtaButton slug="/contact/">Get Started</CtaButton>}
        imgMargin="-80px 0px -54px -40px"
        graphic={data.g1.childImageSharp.gatsbyImageData}
        maxWidth={480}
        rightPad="20px"
        textMax="550px"
        accent="#2bb1ff"
        blockW="1370px"
      />

      <HeaderBlock pad="5rem" mobilePad="3.6rem" />

      <InfiniteCards
        cards={homePageData.infiniteCards1}
        pad="8rem 0 5rem"
        headerText="How does it work?"
        activeCardHeight={290}
        subText={
          <p>
            We take the top 5% of university talent, train them, and embed them
            onto your team
          </p>
        }
      />

      <HeaderBlock
        pad="12rem 0 8rem"
        mobilePad="8rem 1.8rem 4.6rem"
        headerText="An end to end managed service, so you can spend more time executing"
        headMax={1100}
      />

      <SplitScreen
        first
        head="The top minds with the latest skillsets"
        sub="The intellectual firepower of universities represents a group that is uniquely positioned to solve a business’ problems. ProMazo’s Fellows are highly adaptable, continuous learners who are experts in creative and technical fields ranging from Design to Software Development, ML/AI, and Big Data."
        graphic={data.g2.childImageSharp.gatsbyImageData}
        gWidth={523}
        gHeight={751}
        pad="0 23px 9rem"
        textMax="510px"
        innerMiddle="9%"
        innerMax="1030px"
      />

      <SplitScreen
        reverse
        head="Managed for you"
        sub="In addition to having the best and brightest, a ProMazo Manager oversees the day-to-day work of each team. These managers are experts in remote work and ensure the productivity of your ProMazo teams. We also take care of all the sourcing and back office activities, freeing you to focus on driving results with your ProMazo Fellows."
        graphic={data.g3.childImageSharp.gatsbyImageData}
        gWidth={613}
        gHeight={617}
        pad="9rem 23px"
        innerMax="1100px"
        textMax="480px"
      />
      <SplitScreen
        head="Flexibility & Speed"
        sub="ProMazo enables leaders to efficiently allocate their resources while allowing them to dynamically adjust the bandwidth and skill sets of their teams using ProMazo Fellows. Unlike many traditional firms, it takes us days not months to source a Fellow for your team, and you do not have to worry about any of the sourcing process. From talent identification to interviews and onboarding, we have it handled. Best of all you get the top 5% of talent at a variable rate without the premium of typical staffing firms."
        graphic={data.g4.childImageSharp.gatsbyImageData}
        gWidth={500}
        gHeight={750}
        pad="9rem 23px 12rem"
        innerMax="1150px"
        textMax="500px"
      />
      {/* 
      <OverlapBlock
        head="Hear about how we help some of the worlds top companies solve their challenges"
        buttons={<CtaButton>ProMazo Case Studies</CtaButton>}
        imgMargin="0"
        graphic={data.g5.childImageSharp.fluid}
        maxWidth={753}
        maxHeight={653}
        rightPad="0"
        textMax="650px"
        accent="#2bb1ff"
        blockW="1400px"
        buttonPos="flex-start"
      /> */}

      <HeaderBlock
        pad="4rem 0 8rem"
        mobilePad="4rem 1.8rem 3.2rem"
        headerText="ProMazo’s unique workforce transforms teams in three key ways:"
        headMax={1070}
      />

      <CardlessTrifold iconsArr={homePageData.cardless} />

      <HeaderBlock pad="8rem" mobilePad="3.6rem" />

      <OverlapBlock
        head="Ability is everywhere, but opportunity is not"
        sub="Our mission is to ensure that people from diverse sets of backgrounds have the chance to bring their perspectives to firms. Through our engagements and partnerships with HR and D&I departments at companies, we give Fellows from underrepresented backgrounds an opportunity to showcase their talents and break through to realize their full potential."
        buttons={<CtaButton slug="/contact/">Get Started</CtaButton>}
        imgMargin="-60px 0px -60px 0"
        graphic={data.g6.childImageSharp.gatsbyImageData}
        maxWidth={747}
        maxHeight={731}
        rightPad="95px"
        textMax="652px"
        blockW="1440px"
        accent="#2bb1ff"
      />
      <HerolessQuote
        avatar={data.avatar.childImageSharp.gatsbyImageData}
        pad="12rem 1.8rem"
        mobilePad="6rem 1.8rem"
        quote={
          "ProMazo's system allowed us to quickly integrate into the company's workflow and teams. As a result, we were able to produce a high impact product that our client still utilizes to this day as part of their everyday operations."
        }
      />
    </Layout>
  )
}

export default IndexPage
